import React, { useEffect, useRef } from 'react'
import { SearchResult, SearchCategory } from './types'

const CATEGORY_LABELS: Record<SearchCategory, string> = {
  screen: 'Screens',
  collection: 'Collections',
  component: 'Components',
}

const CATEGORY_ORDER: SearchCategory[] = ['screen', 'collection', 'component']

interface Props {
  results: SearchResult[]
  activeIndex: number
  onSelect: (result: SearchResult) => void
  flatResults: SearchResult[]
}

const CommandPaletteResults: React.FC<Props> = ({
  results,
  activeIndex,
  onSelect,
  flatResults,
}) => {
  const activeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (results.length === 0) {
    return <div className="command-palette-empty">No results found</div>
  }

  const grouped = CATEGORY_ORDER.map(category => ({
    category,
    label: CATEGORY_LABELS[category],
    items: results.filter(r => r.category === category),
  })).filter(g => g.items.length > 0)

  return (
    <div className="command-palette-results">
      {grouped.map(group => (
        <div key={group.category} className="command-palette-group">
          <div className="command-palette-group-header">{group.label}</div>
          {group.items.map(item => {
            const flatIdx = flatResults.indexOf(item)
            const isActive = flatIdx === activeIndex

            return (
              <div
                key={item.id}
                ref={isActive ? activeRef : undefined}
                role="option"
                aria-selected={isActive}
                tabIndex={-1}
                className={`command-palette-item ${isActive ? 'active' : ''}`}
                onClick={() => onSelect(item)}
                onKeyDown={e => {
                  if (e.key === 'Enter') onSelect(item)
                }}
                onMouseDown={e => e.preventDefault()}
              >
                <span
                  className={`command-palette-icon command-palette-icon--${item.category}`}
                />
                <span className="command-palette-item-name">{item.name}</span>
                {item.parentName && (
                  <span className="command-palette-item-parent">
                    {item.parentName}
                  </span>
                )}
                <span
                  className={`command-palette-badge command-palette-badge--${item.category}`}
                >
                  {CATEGORY_LABELS[item.category]}
                </span>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default CommandPaletteResults
